

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection, UiLabeledValue } from 'components/v2/record/DetailSection';

const TravelInvoiceDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [invoice, setInvoice] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        if (id) {
            fetchInvoiceDetail();
            fetchLineItems();
        }
    }, [id]);

    const fetchInvoiceDetail = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/travel-invoices/${id}`);
            setInvoice(response.data);
        } catch (error) {
            console.error('Error fetching travel invoice details:', error);
            setSnackbar({ open: true, message: 'Error loading travel invoice details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchLineItems = async () => {
        try {
            const response = await axiosInstance.get(`/travel-invoices/${id}/line-items/`);
            setLineItems(response.data);
        } catch (error) {
            console.error('Error fetching line items:', error);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setInvoice(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/travel-invoices/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Travel Invoice updated successfully', severity: 'success' });
            fetchInvoiceDetail();
        } catch (error) {
            console.error('Error updating travel invoice:', error);
            setSnackbar({ open: true, message: 'Error updating travel invoice', severity: 'error' });
        }
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({ open: true, message: `Unable to find user info for ${userName}.`, severity: 'info' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this travel invoice?')) {
            try {
                await axiosInstance.delete(`/travel-invoices/${id}`);
                setSnackbar({ open: true, message: 'Travel Invoice deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/travel-invoices'), 1000);
            } catch (error) {
                console.error('Error deleting travel invoice:', error);
                setSnackbar({ open: true, message: 'Error deleting travel invoice', severity: 'error' });
            }
        }
    };

    const formatCurrency = (amount, currency = 'GBP') => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'GBP',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(parseFloat(amount));
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} md={6} key={item.field}>
                        {['created_by_name', 'updated_by_name'].includes(item.field) ? (
                            <UiLabeledValue label={item.label}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{item.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={() => handleUserClick(item.field === 'created_by_name' ? invoice.created_by : invoice.updated_by, invoice[item.field])}
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{ cursor: 'pointer', ml: 1 }}
                                >
                                    {invoice[item.field]}
                                </MDTypography>
                            </MDBox>
                            </UiLabeledValue>
                        ) : (
                            <InlineEditField
                                label={item.label}
                                value={invoice?.[item.field]}
                                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                                isEditing={isEditing}
                                readOnly={item.readOnly || false}
                                renderValue={item.renderValue}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox mb={3} p={3} borderRadius="lg" sx={{ backgroundColor: '#ffffff', boxShadow: 3 }}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}><Skeleton variant="rectangular" height={400} /></MDBox>
                
            </DashboardLayout>
        );
    }

    if (!invoice) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}><MDTypography>Travel Invoice not found</MDTypography></MDBox>
                
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                                display="flex" justifyContent="space-between" alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">{invoice.name || `Invoice #${invoice.id}`}</MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>Travel Invoice Details</MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchInvoiceDetail();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Travel Invoice">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete Travel Invoice">
                                        <IconButton
                                            onClick={handleDelete}
                                            sx={commonIconButtonStyles}
                                        >
                                            <Delete sx={commonIconStyles} />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Basic Information", [
                                    { label: "Invoice Number", field: "name" },
                                    { label: "Record Type", field: "record_type" },
                                    { label: "Order", field: "order_id", readOnly: true, renderValue: (val) => invoice.order?.name || val },
                                    { label: "Amount", field: "amount", renderValue: (val) => formatCurrency(val, invoice.currency) },
                                    { label: "Currency", field: "currency" },
                                    { label: "Status", field: "status" },
                                    { label: "Invoice Date", field: "invoice_date", renderValue: (val) => formatDate(val, 'date') },
                                    { label: "Due Date", field: "due_date", renderValue: (val) => formatDate(val, 'date') },
                                    { label: "Reference No", field: "ref_no" },
                                ])}

                                {invoice.record_type === 'Customer' && (
                                    renderSection("Customer Information", [
                                        { label: "Customer Address", field: "customer_address" },
                                        { label: "Payment Template", field: "payment_template_id", readOnly: true, renderValue: (val) => invoice.payment_template?.name || val },
                                        { label: "Payment URL", field: "payment_url" },
                                    ])
                                )}

                                {(invoice.record_type === 'Guide' || invoice.record_type === 'Merchant' || invoice.record_type === 'Influencer') && (
                                    renderSection("Supplier Information", [
                                        { label: "Account", field: "account_id", readOnly: true, renderValue: (val) => invoice.account?.name || val },
                                        { label: "Payment Term", field: "payment_term_id", readOnly: true, renderValue: (val) => invoice.payment_term?.name || val },
                                        { label: "Departure Date", field: "departure_date_id", readOnly: true, renderValue: (val) => invoice.departure_date?.name || val },
                                        { label: "Paid Amount", field: "paid_amount", renderValue: (val) => formatCurrency(val, invoice.currency) },
                                        { label: "Balance Amount", field: "balance_amount", renderValue: (val) => formatCurrency(val, invoice.currency) },
                                    ])
                                )}

                                {invoice.record_type === 'Guide' && invoice.guide_allocation_id && (
                                    renderSection("Guide Allocation", [
                                        { label: "Guide Allocation", field: "guide_allocation_id", readOnly: true, renderValue: (val) => invoice.guide_allocation?.name || val },
                                    ])
                                )}

                                {lineItems.length > 0 && (
                                    <MDBox
                                        mb={3}
                                        p={3}
                                        borderRadius="lg"
                                        sx={{
                                            backgroundColor: 'white',
                                            boxShadow: 3,
                                            maxWidth: '100%',
                                            width: 'auto'
                                        }}
                                    >
                                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                            <MDTypography variant="h6" fontWeight="medium" color="dark">
                                                Invoice Line Items
                                            </MDTypography>
                                        </MDBox>

                                        <MDBox
                                            sx={{
                                                mt: 3,
                                                mb: 3,
                                                border: "1px solid #dee2e6",
                                                borderRadius: "0.75rem",
                                                overflow: "auto",
                                                maxHeight: "400px",
                                            }}
                                        >
                                            {/* Header */}
                                            <MDBox
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: "20% 15% 15% 15% 10% 12% 13%",
                                                    bgcolor: "#f8f9fa",
                                                    borderBottom: "1px solid #dee2e6",
                                                    p: 2,
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 1,
                                                    minWidth: 'max-content'
                                                }}
                                            >
                                                <MDBox sx={{ px: 1 }}>
                                                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                                        Name
                                                    </MDTypography>
                                                </MDBox>
                                                <MDBox sx={{ px: 1, textAlign: "right" }}>
                                                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                                        Order
                                                    </MDTypography>
                                                </MDBox>
                                                <MDBox sx={{ px: 1, textAlign: "right" }}>
                                                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                                        Customer
                                                    </MDTypography>
                                                </MDBox>
                                                <MDBox sx={{ px: 1, textAlign: "right" }}>
                                                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                                        Amount
                                                    </MDTypography>
                                                </MDBox>
                                                <MDBox sx={{ px: 1, textAlign: "right" }}>
                                                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                                        Currency
                                                    </MDTypography>
                                                </MDBox>
                                                <MDBox sx={{ px: 1, textAlign: "right" }}>
                                                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                                        Status
                                                    </MDTypography>
                                                </MDBox>
                                                <MDBox sx={{ px: 1, textAlign: "right" }}>
                                                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                                        Invoice Date
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>

                                            {/* Body */}
                                            {lineItems.map((item, index) => (
                                                <MDBox
                                                    key={index}
                                                    sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: "20% 15% 15% 15% 10% 12% 13%",
                                                        borderBottom: '1px solid #dee2e6',
                                                        p: 2,
                                                        '&:hover': { backgroundColor: '#f8f9fa' },
                                                        transition: 'background-color 0.2s',
                                                        minWidth: 'max-content'
                                                    }}
                                                >
                                                    <MDBox sx={{ px: 1 }}>
                                                        <MDTypography variant="button" color="text">
                                                            {item.name || `Line Item #${item.id}` || '-'}
                                                        </MDTypography>
                                                    </MDBox>
                                                    <MDBox sx={{ px: 1, textAlign: "right" }}>
                                                        <MDTypography variant="button" color="text">
                                                            {item.order?.name || item.order_id || '-'}
                                                        </MDTypography>
                                                    </MDBox>
                                                    <MDBox sx={{ px: 1, textAlign: "right" }}>
                                                        <MDTypography variant="button" color="text">
                                                            {item.customer?.name || item.customer_id || '-'}
                                                        </MDTypography>
                                                    </MDBox>
                                                    <MDBox sx={{ px: 1, textAlign: "right" }}>
                                                        <MDTypography variant="button" color="text">
                                                            {formatCurrency(parseFloat(item.amount || 0), item.currency || invoice.currency)}
                                                        </MDTypography>
                                                    </MDBox>
                                                    <MDBox sx={{ px: 1, textAlign: "right" }}>
                                                        <MDTypography variant="button" color="text">
                                                            {item.currency || invoice.currency || '-'}
                                                        </MDTypography>
                                                    </MDBox>
                                                    <MDBox sx={{ px: 1, textAlign: "right" }}>
                                                        <MDTypography variant="button" color="text">
                                                            {item.status || '-'}
                                                        </MDTypography>
                                                    </MDBox>
                                                    <MDBox sx={{ px: 1, textAlign: "right" }}>
                                                        <MDTypography variant="button" color="text">
                                                            {item.invoice_date ? formatDate(item.invoice_date) : '-'}
                                                        </MDTypography>
                                                    </MDBox>
                                                </MDBox>
                                            ))}

                                            {/* Footer */}
                                            <MDBox
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: "85% 15%",
                                                    borderTop: '2px solid #dee2e6',
                                                    p: 2,
                                                    minWidth: 'max-content',
                                                    backgroundColor: 'white'
                                                }}
                                            >
                                                <MDBox textAlign="right">
                                                    <MDTypography variant="button" fontWeight="medium" color="dark">
                                                        Total
                                                    </MDTypography>
                                                </MDBox>
                                                <MDBox textAlign="right">
                                                    <MDTypography variant="button" fontWeight="medium" color="dark">
                                                        {formatCurrency(
                                                            lineItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0),
                                                            invoice.currency
                                                        )}
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>
                                        </MDBox>
                                    </MDBox>
                                )}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name" },
                                    { label: "Modified By", field: "updated_by_name" },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TravelInvoiceDetail;

